// src/features/admin/pages/AdminUsersPage.tsx
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify } from "@/ui/notify";
import { customConfirm } from "@/ui/confirm";
import "../admin.css";
import { COLOR, FW, RADIUS } from "@/ui/tokens";

type User = { id:number; email:string; name:string; nickname?:string; status:"ACTIVE"|"SUSPENDED"|"DELETED"; created_at:string; roles:string[] };

export default function AdminUsersPage() {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [size] = useState(20);
    const [items, setItems] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const totalPages = Math.max(1, Math.ceil(total / size));

    const load = async () => {
        setLoading(true);
        try {
            const data = await api(`/admin/users?page=${page}&size=${size}&q=${encodeURIComponent(q)}`);
            setItems(data.items || []); setTotal(Number(data.total || 0));
        } finally { setLoading(false); }
    };
    useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [page]);

    const search = (e: React.FormEvent) => { e.preventDefault(); setPage(1); load(); };

    const patch = async (id:number, payload:any) => {
        await api(`/admin/users/${id}`, { method:"PATCH", json: payload });
        await load();
    };

    const unregister = async (id:number, email:string) => {
        const ok = await customConfirm(
            `[${email}] 회원을 탈퇴 처리할까요?\n\n` +
            `■ 개인정보 처리\n` +
            `· 휴대폰 번호(암호화), 생일 → 30일 후 파기\n` +
            `· 본인확인 기록 → 6개월 보관 (정보통신망법)\n` +
            `· 접속 로그 → 3개월 보관 (통신비밀보호법)\n\n` +
            `■ 게시물(리뷰 등) 처리\n` +
            `· 내용은 익명화 형태로 유지됩니다.\n` +
            `· 닉네임·프로필 등 식별 정보는 즉시 비식별화 처리됩니다.\n\n` +
            `탈퇴 후 로그인이 불가하며 복구할 수 없습니다.`,
            "탈퇴처리"
        );
        if (!ok) return;
        await api(`/admin/users/${id}/unregister`, {
            method: "POST",
            json: { anonymize_posts: true },
        });
        notify("탈퇴 처리 완료");
        await load();
    };

    const grantRole = async (id:number, code:string) => {
        await api(`/admin/users/${id}/roles`, { method:"POST", json:{ code }});
        notify(`${code} 부여 완료`);
        await load();
    };

    const revokeRole = async (id:number, code:string) => {
        await api(`/admin/users/${id}/roles`, { method:"DELETE", json:{ code }});
        notify(`${code} 해제 완료`);
        await load();
    };

    return (
        <div className="adm-page">
            <h2>Users</h2>
            <form onSubmit={search} className="adm-search-form">
                <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="이메일/닉네임/이름"
                       className="adm-input" style={{ flex: 1 }} />
                <button className="adm-btn">검색</button>
            </form>

            <div style={{ height: 12 }} />
            {loading ? <div>로딩…</div> : (
                <table className="adm-table">
                    <thead>
                        <tr><th>ID</th><th>Email</th><th>Name</th><th>Nick</th><th>Role</th><th>Status</th><th>액션</th></tr>
                    </thead>
                    <tbody>
                    {items.map(u => (
                        <tr key={u.id}>
                            <td>{u.id}</td>
                            <td>{u.email}</td>
                            <td>
                                <input defaultValue={u.name} onBlur={(e)=>patch(u.id,{ name:e.currentTarget.value })}
                                       className="adm-input-cell" />
                            </td>
                            <td>
                                <input defaultValue={u.nickname||""} onBlur={(e)=>patch(u.id,{ nickname:e.currentTarget.value })}
                                       className="adm-input-cell" />
                            </td>
                            <td>
                                {(u.roles || []).length === 0
                                    ? <span style={{ color:COLOR.textLight, fontSize:12 }}>USER</span>
                                    : (u.roles || []).map(r => (
                                        <span key={r} style={{
                                            display:"inline-block", padding:"2px 7px", borderRadius:RADIUS.full, fontSize:11, fontWeight:FW.bold, marginRight:3,
                                            background: r==="SUPER_ADMIN"?"#18181b": r==="ADMIN"?"#2563eb": r==="AUTHOR"?"#7c3aed": r==="MONITORING"?"#0891b2":"#e4e4e7",
                                            color: r==="SUPER_ADMIN"||r==="ADMIN"||r==="AUTHOR"||r==="MONITORING" ? "#fff" : "#555",
                                        }}>{r}</span>
                                    ))
                                }
                            </td>
                            <td>
                                {u.status === "DELETED"
                                    ? <span style={{ fontSize:12, fontWeight:FW.bold, color:COLOR.error }}>DELETED</span>
                                    : <select defaultValue={u.status} onChange={(e)=>patch(u.id,{ status:e.currentTarget.value })}
                                            className="adm-select-cell"
                                            style={{ color: u.status==="SUSPENDED" ? "#e67e22" : undefined }}>
                                        <option value="ACTIVE">ACTIVE</option>
                                        <option value="SUSPENDED">SUSPENDED</option>
                                    </select>
                                }
                            </td>
                            <td style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                                {u.roles.includes("MONITORING")
                                    ? <button onClick={()=>revokeRole(u.id,"MONITORING")} className="adm-btn-outline" style={{ color:"#0891b2" }}>MON−</button>
                                    : <button onClick={()=>grantRole(u.id,"MONITORING")} className="adm-btn-outline">MON+</button>
                                }
                                {u.roles.includes("AUTHOR")
                                    ? <button onClick={()=>revokeRole(u.id,"AUTHOR")} className="adm-btn-outline" style={{ color:"#7c3aed" }}>AUTHOR−</button>
                                    : <button onClick={()=>grantRole(u.id,"AUTHOR")} className="adm-btn-outline">AUTHOR+</button>
                                }
                                {u.roles.includes("ADMIN")
                                    ? <button onClick={()=>revokeRole(u.id,"ADMIN")} className="adm-btn-outline" style={{ color:"#2563eb" }}>ADMIN−</button>
                                    : <button onClick={()=>grantRole(u.id,"ADMIN")} className="adm-btn-outline">ADMIN+</button>
                                }
                                <button
                                    onClick={()=>unregister(u.id, u.email)}
                                    className="adm-btn-outline"
                                    disabled={u.status==="DELETED"}
                                    style={{ color: u.status==="DELETED" ? "#aaa" : "#d00", borderColor: u.status==="DELETED" ? "#ddd" : "#fca5a5" }}
                                >탈퇴처리</button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}

            <div className="adm-pagination">
                <button disabled={page<=1} onClick={()=>setPage(1)}>⏮</button>
                <button disabled={page<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>◀</button>
                <span>{page} / {totalPages}</span>
                <button disabled={page>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>▶</button>
                <button disabled={page>=totalPages} onClick={()=>setPage(totalPages)}>⏭</button>
            </div>
        </div>
    );
}
